import React from 'react';
import PropTypes, { InferProps } from 'prop-types';
import styled from '@emotion/styled';
import {
  CardOverline, CardDate, CardBodyText,
  CardTextContainer, CardContainer, FullWidthImageContainer,
} from '../Card';
import Headline5 from '../Typography/Heading/Headline5';
import Labels, { FeaturedLabel, MemberAccessLabel } from '../Card/Labels';
import cardPropertyTypes from './propertyTypes';

const StyledHeadline5 = styled(Headline5)<
{
  hasIcon: boolean;
}
>`
  && {
    margin: 0 0 8px;
    ${(properties : { hasIcon: boolean }) => properties.hasIcon && `
      display: flex;
      justify-content: space-between;
    `
}
  }
`;

const mediumCardPropertyTypes = {
  ...cardPropertyTypes,
  children: PropTypes.node,
  icon: PropTypes.node,
  featured: PropTypes.bool,
  featuredLabelText: PropTypes.string,
  memberAccess: PropTypes.bool,
  memberAccessLabelText: PropTypes.string,
  logoLetterbox: PropTypes.bool,
  isPressRelease: PropTypes.bool,
  isPartnerContent: PropTypes.bool,
};

type MediumCardProperties = InferProps<typeof mediumCardPropertyTypes> & {
  featured?: boolean;
  memberAccess?: boolean;
  withPageMargins?: boolean;
  logoLetterbox?: boolean;
  isPressRelease?: boolean;
  isPartnerContent?: boolean;
};

const MediumCard = ({
  title,
  category,
  insertedAt,
  image,
  description,
  labels,
  children,
  icon,
  featured,
  featuredLabelText,
  memberAccess,
  memberAccessLabelText,
  withPageMargins,
  logoLetterbox,
  isPressRelease,
  isPartnerContent,
} : MediumCardProperties) => {
  const featuredValue = featured ?? false;

  return (
    <CardContainer className="card-container">
      {image && (
      <FullWidthImageContainer
        featured={featuredValue}
        withPageMargins={withPageMargins ?? false}
        logoLetterbox={logoLetterbox ?? false}
      >
        {image}
      </FullWidthImageContainer>
      )}
      <CardTextContainer>
        { category && (
          <CardOverline>
            <div>
              {featuredValue && (
                <FeaturedLabel>{featuredLabelText}</FeaturedLabel>
              )}
              {(memberAccess ?? false) && (
                <MemberAccessLabel>{memberAccessLabelText}</MemberAccessLabel>
              )}
            </div>
            <Labels
              category={category}
              labels={labels ?? []}
              isPressRelease={isPressRelease ?? false}
              isPartnerContent={isPartnerContent ?? false}
            />
          </CardOverline>
        )}
        <StyledHeadline5
          tag="h2"
          hasIcon={Boolean(icon)}
        >
          {title}
          {icon}
        </StyledHeadline5>
        {
          description && (
            <CardBodyText>
              {description}
            </CardBodyText>
          )
        }
        {insertedAt && <CardDate>{insertedAt}</CardDate>}
        { children }
      </CardTextContainer>
    </CardContainer>
  );
};

MediumCard.propTypes = mediumCardPropertyTypes;

export default MediumCard;
